import { useState, useEffect, useRef } from 'react';
import { axiosInstance } from '../App';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Camera, X, Trash2, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { AddressPicker, rememberAddress } from './AddressPicker';
import { createPortal } from 'react-dom';

// Sequential image compression for mobile-safe uploads — iteratively downscales +
// lowers quality until the file fits the target size (~10MB), without losing
// noticeable quality. Uses URL.createObjectURL (no base64 copy) so 50-80MB
// ProRAW / HEIF originals don't OOM-crash on mobile.
const compressImage = (file, targetBytes = 10 * 1024 * 1024) => {
  return new Promise((resolve) => {
    if (!file.type || !file.type.startsWith('image/')) { resolve(file); return; }
    if (file.size <= 2 * 1024 * 1024) { resolve(file); return; }
    const objUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = async () => {
      const attempts = [
        { maxW: 2560, q: 0.92 },
        { maxW: 2560, q: 0.85 },
        { maxW: 2048, q: 0.85 },
        { maxW: 1920, q: 0.82 },
        { maxW: 1920, q: 0.75 },
        { maxW: 1600, q: 0.75 },
      ];
      const canvas = document.createElement('canvas');
      let lastBlob = null;
      try {
        for (let i = 0; i < attempts.length; i++) {
          const { maxW, q } = attempts[i];
          const ratio = Math.min(maxW / img.width, 1);
          canvas.width = Math.round(img.width * ratio);
          canvas.height = Math.round(img.height * ratio);
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          const blob = await new Promise((r) => canvas.toBlob(r, 'image/jpeg', q));
          if (!blob) continue;
          lastBlob = blob;
          if (blob.size <= targetBytes) break;
        }
      } catch {/* noop */}
      canvas.width = 0; canvas.height = 0;
      URL.revokeObjectURL(objUrl);
      if (lastBlob && lastBlob.size < file.size) {
        const cleanName = (file.name || 'foto.jpg').replace(/\.(heic|heif|tiff|png|webp|dng)$/i, '.jpg');
        resolve(new File([lastBlob], cleanName, { type: 'image/jpeg' }));
      } else {
        resolve(file);
      }
    };
    img.onerror = () => { URL.revokeObjectURL(objUrl); resolve(file); };
    img.src = objUrl;
  });
};

const STORAGE_KEY = 'quickPhotoLastUsed';

const QuickPhotoFAB = () => {
  const [open, setOpen] = useState(false);
  const [photos, setPhotos] = useState([]); // File[]
  const [previews, setPreviews] = useState([]); // dataURL[]
  const [clients, setClients] = useState([]);
  const [clientName, setClientName] = useState('');
  const [addressLabel, setAddressLabel] = useState('');
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState('');
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  // Load clients once
  useEffect(() => {
    let canceled = false;
    axiosInstance.get('/clients/all').then(r => { if (!canceled) setClients(r.data || []); }).catch(() => {});
    return () => { canceled = true; };
  }, []);

  // Restore last used client+address from localStorage when dialog opens
  useEffect(() => {
    if (open) {
      try {
        const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
        if (saved.clientName) setClientName(saved.clientName);
        if (saved.addressLabel) setAddressLabel(saved.addressLabel);
      } catch {/* noop */}
    }
  }, [open]);

  const selectedClient = clients.find(c => c.name === clientName);
  const addressOptions = selectedClient?.addresses || [];

  const onFilesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setPhotos(prev => [...prev, ...files]);
    // Generate previews (image dataURL or video placeholder)
    files.forEach(f => {
      if (f.type && f.type.startsWith('video/')) {
        setPreviews(prev => [...prev, { type: 'video', name: f.name }]);
      } else {
        const reader = new FileReader();
        reader.onload = (ev) => setPreviews(prev => [...prev, { type: 'image', src: ev.target.result }]);
        reader.readAsDataURL(f);
      }
    });
    setOpen(true);
    e.target.value = '';
  };

  const removePhoto = (idx) => {
    setPhotos(prev => prev.filter((_, i) => i !== idx));
    setPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!clientName) { toast.error('Selecteer een bedrijf'); return; }
    if (addressOptions.length > 0 && !addressLabel) { toast.error('Selecteer een adres'); return; }
    if (photos.length === 0) { toast.error('Geen foto geselecteerd'); return; }

    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ clientName, addressLabel })); } catch {/* noop */}
    rememberAddress(clientName, addressOptions.find(a => (a.label || a.address) === addressLabel)?.address || addressLabel);

    setUploading(true);

    // Get Cloudinary signature for direct upload (handles big videos)
    let sig;
    try {
      const fd = new FormData();
      fd.append('folder', `cardoso_bouw/${clientName.replace(/\s+/g, '_')}`);
      const r = await axiosInstance.post('/photos/cloudinary-signature', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      sig = r.data;
    } catch {
      toast.error('Niet kunnen verbinden met upload service');
      setUploading(false);
      return;
    }

    let ok = 0, fail = 0;
    for (let i = 0; i < photos.length; i++) {
      const file = photos[i];
      const isVideo = file.type && file.type.startsWith('video/');
      setProgress(`${isVideo ? 'Video' : 'Foto'} ${i + 1}/${photos.length}: ${isVideo ? 'voorbereiden' : 'comprimeren'}...`);
      let toSend = file;
      if (!isVideo) {
        try { toSend = await compressImage(toSend); } catch { /* fallback */ }
      }
      setProgress(`${isVideo ? 'Video' : 'Foto'} ${i + 1}/${photos.length}: uploaden (${(toSend.size/1024/1024).toFixed(1)} MB)...`);

      // Direct upload to Cloudinary
      const cloudFD = new FormData();
      cloudFD.append('file', toSend);
      cloudFD.append('api_key', sig.api_key);
      cloudFD.append('timestamp', sig.timestamp);
      cloudFD.append('signature', sig.signature);
      cloudFD.append('folder', sig.folder);

      let cloudResult;
      try {
        const resourceType = isVideo ? 'video' : 'image';
        const res = await fetch(
          `https://api.cloudinary.com/v1_1/${sig.cloud_name}/${resourceType}/upload`,
          { method: 'POST', body: cloudFD }
        );
        if (!res.ok) throw new Error(`Cloudinary ${res.status}`);
        cloudResult = await res.json();
      } catch {
        fail++;
        continue;
      }

      try {
        await axiosInstance.post('/photos/save-direct-upload', {
          client_name: clientName,
          address: addressLabel || '',
          description: description || '',
          cloudinary_url: cloudResult.secure_url,
          cloudinary_public_id: cloudResult.public_id,
          cloudinary_thumbnail: isVideo
            ? `https://res.cloudinary.com/${sig.cloud_name}/video/upload/c_fill,g_auto,h_400,w_400,q_auto:good,f_jpg,so_2/${cloudResult.public_id}.jpg`
            : `https://res.cloudinary.com/${sig.cloud_name}/image/upload/c_fill,g_auto,h_400,w_400,q_auto:good,f_auto/${cloudResult.public_id}`,
          file_type: file.type || 'image/jpeg',
          filename: file.name,
          is_video: isVideo,
          duration: cloudResult.duration || 0,
          bytes: cloudResult.bytes || toSend.size,
        });
        ok++;
      } catch { fail++; }
    }
    setUploading(false);
    setProgress('');
    if (ok > 0 && fail === 0) {
      toast.success(`✅ ${ok} bestand(en) verzonden!`);
    } else if (ok > 0 && fail > 0) {
      toast.warning(`${ok} verzonden, ${fail} mislukt.`);
    } else {
      toast.error('Geen bestanden verzonden');
    }
    if (ok > 0) {
      setPhotos([]); setPreviews([]); setDescription('');
      setOpen(false);
    }
  };

  return (
    <>
      {/* Hidden inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*,video/*"
        capture="environment"
        className="hidden"
        onChange={onFilesSelected}
        data-testid="quick-photo-camera-input"
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        className="hidden"
        onChange={onFilesSelected}
        data-testid="quick-photo-gallery-input"
      />

      {/* Floating button */}
      {createPortal(
        <button
          onClick={() => cameraInputRef.current?.click()}
          aria-label="Snelle foto"
          className="fixed bottom-20 right-4 sm:bottom-6 sm:right-6 bg-[#3da5a5] hover:bg-[#2d8585] text-white rounded-full p-4 shadow-2xl border-4 border-white transition-transform active:scale-95 hover:scale-105"
          style={{ zIndex: 9990 }}
          data-testid="quick-photo-fab"
        >
          <Camera className="w-7 h-7" />
        </button>,
        document.body
      )}

      {/* Dialog after selecting/taking photo(s) */}
      <Dialog open={open} onOpenChange={(o) => {
        if (uploading) return;
        setOpen(o);
        if (!o) { setPhotos([]); setPreviews([]); setDescription(''); }
      }}>
        <DialogContent className="bg-white max-w-lg max-h-[90dvh] overflow-y-auto" data-testid="quick-photo-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-800">
              <Camera className="w-5 h-5 text-[#3da5a5]" />
              Snelle foto ({photos.length})
            </DialogTitle>
            <DialogDescription className="text-gray-500 text-sm">
              Maak een foto of kies uit galerij, koppel aan klant en verstuur direct.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Photo previews grid */}
            {previews.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {previews.map((p, i) => (
                  <div key={i} className="relative aspect-square bg-gray-100 rounded overflow-hidden">
                    {p.type === 'video' ? (
                      <div className="w-full h-full flex flex-col items-center justify-center bg-gray-900 text-white p-2">
                        <Camera className="w-8 h-8 mb-1" />
                        <span className="text-[10px] text-center break-all line-clamp-2">🎬 {p.name}</span>
                      </div>
                    ) : (
                      <img src={p.src} alt={`foto ${i+1}`} className="w-full h-full object-cover" />
                    )}
                    <button
                      type="button"
                      onClick={() => removePhoto(i)}
                      disabled={uploading}
                      className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 shadow disabled:opacity-50"
                      aria-label="Verwijderen"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => cameraInputRef.current?.click()}
                disabled={uploading}
                className="flex-1 border-gray-300"
                data-testid="quick-photo-add-camera"
              >
                <Camera className="w-4 h-4 mr-1" /> Foto maken
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => galleryInputRef.current?.click()}
                disabled={uploading}
                className="flex-1 border-gray-300"
                data-testid="quick-photo-add-gallery"
              >
                <Plus className="w-4 h-4 mr-1" /> Galerij
              </Button>
            </div>

            <div>
              <Label className="text-gray-700">Klant / adres * <span className="text-xs text-gray-400 font-normal">— typ naam, straat of plaats</span></Label>
              <AddressPicker clients={clients} clientName={clientName} address={addressLabel && selectedClient ? (addressOptions.find(a => (a.label || a.address) === addressLabel)?.address || addressLabel) : ''} disabled={uploading} testId="quick-photo-picker"
                onChange={({ clientName: cn, address, label }) => { setClientName(cn); setAddressLabel(cn ? (label || address || '') : ''); }} />
            </div>


            <div>
              <Label className="text-gray-700">Omschrijving (optioneel)</Label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={uploading}
                placeholder="Bijv. voor/na, materiaal gebruikt..."
                className="w-full bg-gray-50 border border-gray-200 rounded-lg p-2 text-sm h-16 disabled:opacity-50"
                data-testid="quick-photo-description"
              />
            </div>

            {progress && (
              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-800 text-center" data-testid="quick-photo-progress">
                {progress}
              </div>
            )}

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => { if (!uploading) { setOpen(false); setPhotos([]); setPreviews([]); } }}
                disabled={uploading}
                className="flex-1"
              >
                <X className="w-4 h-4 mr-1" /> Annuleren
              </Button>
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={uploading || photos.length === 0 || !clientName}
                className="flex-1 bg-[#3da5a5] hover:bg-[#2d8585]"
                data-testid="quick-photo-submit"
              >
                {uploading ? (
                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Bezig...</>
                ) : (
                  `Versturen (${photos.length})`
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default QuickPhotoFAB;
